//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import { useQuery } from "@tanstack/react-query";
import apiClients from "../../api/apiclients";
import { useDashboardContext } from "../DashboardContextProvider";

export const useFetchOrganizationDeployments = ({
  pageNumber = 0,
  pageSize = 10,
  keepPreviousData = true,
  // Optional extra gate, ANDed with the existing organization-id check
  // below - lets a caller (e.g. useResolveDeploymentNames) skip the fetch
  // entirely under its own condition while still sharing this hook's exact
  // queryKey/queryFn with every other caller, so react-query dedupes
  // concurrent callers into one request instead of one each. Defaults to
  // true, so every existing caller that does not pass it is unaffected.
  enabled = true,
}: {
  pageNumber: number;
  pageSize: number;
  keepPreviousData?: boolean;
  enabled?: boolean;
}) => {
  const { selectedOrganization } = useDashboardContext();

  const fetchOrganizationDeployments = async () => {
    return await apiClients.dataClient.ListDeploymentsByFilter({
      organization_id: selectedOrganization.id,
      options: {
        page: pageNumber,
        page_size: pageSize,
      },
    });
  };

  return useQuery({
    queryKey: ["organizationDeployments", selectedOrganization.id, pageNumber, pageSize],
    queryFn: () => fetchOrganizationDeployments(),
    enabled: enabled && !!selectedOrganization.id,
    keepPreviousData,
  });
};
